import { inject, Injectable } from '@angular/core';
import { catchError, EMPTY, Observable, of } from 'rxjs';
import { TimeModeEntry } from '@data-terminal/shared-models/time-mode-entry.interface';
import { GLOBAL_SPINNER_HTTP_CONTEXT } from '@data-terminal/utils/http-interceptors';
import { HttpClient } from '@angular/common/http';
import { BACKEND_URL, ORGANIZATION_REQUEST_PREFIX } from '@data-terminal/data-access/injection-tokens';
import { CCAuthService } from '@heidelberg/control-center-frontend-integration/auth';
import { ClientApiError } from '@data-terminal/shared-models/client-api-error';
import { TimeModeErrorId } from '../../models/time-mode-error-id.enum';

@Injectable({
    providedIn: 'root',
})
export class PureTimeApiService {
    readonly #httpClient = inject(HttpClient);
    readonly #backendUrl = inject(BACKEND_URL);
    readonly #orgRequestPrefix = inject(ORGANIZATION_REQUEST_PREFIX);
    readonly #ccAuthService = inject(CCAuthService);

    readonly #getTimeEntriesUrl = (shiftStart: number, deviceId: string): string =>
        `${this.#backendUrl}${this.#orgRequestPrefix}timemode/getRecentByDevice/${shiftStart}/${deviceId}`;

    readonly #getUnsubmittedTimeEntriesUrl = (): string =>
        `${this.#backendUrl}${this.#orgRequestPrefix}timemode/getUnsubmittedEntriesByUser/${this.#ccAuthService.getCurrentUser()?.email}`;

    readonly #updateEntriesUrl = (shiftStart: number, deviceId: string): string =>
        `${this.#backendUrl}${this.#orgRequestPrefix}timemode/updateEntrys/${
            this.#ccAuthService.getCurrentUser()?.email
        }/${shiftStart}/${deviceId}`;

    readonly #deleteEntryUrl = (shiftStart: number, deviceId: string): string =>
        `${this.#backendUrl}${this.#orgRequestPrefix}timemode/deleteEntry/${
            this.#ccAuthService.getCurrentUser()?.email
        }/${shiftStart}/${deviceId}`;

    readonly #startSubmittingUrl = (shiftStart: number, deviceId: string): string =>
        `${this.#backendUrl}${this.#orgRequestPrefix}timemode/startSubmittingEntries/${
            this.#ccAuthService.getCurrentUser()?.email
        }/${shiftStart}/${deviceId}`;

    public getTimeEntries(shiftStart: number, deviceId: string): Observable<TimeModeEntry[]> {
        return this.#httpClient.get<TimeModeEntry[]>(this.#getTimeEntriesUrl(shiftStart, deviceId));
    }

    public getUnsubmittedTimeEntries(): Observable<TimeModeEntry[]> {
        return this.#httpClient.get<TimeModeEntry[]>(this.#getUnsubmittedTimeEntriesUrl());
    }

    public updateEntries(
        entries: TimeModeEntry[],
        shiftStart: number,
        deviceId: string
    ): Observable<TimeModeEntry[] | ClientApiError> {
        return this.#httpClient
            .post<TimeModeEntry[]>(this.#updateEntriesUrl(shiftStart, deviceId), entries, {
                context: GLOBAL_SPINNER_HTTP_CONTEXT,
            })
            .pipe(
                catchError(() =>
                    of({
                        error: true,
                        errorId: TimeModeErrorId.NETWORK_ERROR,
                        errorMsg: 'Network error',
                    } as ClientApiError)
                )
            );
    }

    public deleteEntry(entry: TimeModeEntry, shiftStart: number, deviceId: string): Observable<TimeModeEntry[]> {
        return this.#httpClient
            .delete<TimeModeEntry[]>(this.#deleteEntryUrl(shiftStart, deviceId), {
                body: entry,
                context: GLOBAL_SPINNER_HTTP_CONTEXT,
            })
            .pipe(catchError(() => EMPTY));
    }

    public startSubmittingEntries(shiftStart: number, deviceId: string): Observable<TimeModeEntry[]> {
        return this.#httpClient
            .post<TimeModeEntry[]>(this.#startSubmittingUrl(shiftStart, deviceId), null)
            .pipe(catchError(() => EMPTY));
    }
}
